import * as THREE from 'three';
import { EffectComposer } from 'three/addons/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/addons/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/addons/postprocessing/UnrealBloomPass.js';
import { OutputPass } from 'three/addons/postprocessing/OutputPass.js';
import { ShaderPass } from 'three/addons/postprocessing/ShaderPass.js';
import { SMAAPass } from 'three/addons/postprocessing/SMAAPass.js';
import { GTAOPass } from 'three/addons/postprocessing/GTAOPass.js';
import { BokehPass } from 'three/addons/postprocessing/BokehPass.js';
import { Lensflare, LensflareElement } from 'three/addons/objects/Lensflare.js';
import { Sky } from 'three/addons/objects/Sky.js';
import { $ } from './util';

export const canvas = $('#gl');
export const renderer = new THREE.WebGLRenderer({canvas, antialias:true, powerPreference:'high-performance'});
renderer.setPixelRatio(Math.min(devicePixelRatio, 1.5));
renderer.setSize(innerWidth, innerHeight);
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFShadowMap;
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.55;

export const scene = new THREE.Scene();
export const HAZE = new THREE.Color('#b49e9a');
scene.fog = new THREE.FogExp2(HAZE, 0.00075);

export const camera = new THREE.PerspectiveCamera(72, innerWidth/innerHeight, 0.5, 6000);
camera.rotation.order = 'YXZ';
export const rig = { yaw:0, pitch:-0.14, roll:0, base:new THREE.Vector3(0,104,420), h:104, ground:0, shake:0, bob:0, stepPhase:0, lastStepSign:1, vel:new THREE.Vector3() };

// sky + sun + environment
export const sky = new Sky(); sky.scale.setScalar(200000);
export const su = sky.material.uniforms; su.turbidity.value = 4; su.rayleigh.value = 1.6; su.mieCoefficient.value = 0.004; su.mieDirectionalG.value = 0.8;
export const sunDir = new THREE.Vector3().setFromSphericalCoords(1, THREE.MathUtils.degToRad(90-9), THREE.MathUtils.degToRad(215));
su.sunPosition.value.copy(sunDir);
// keep the sky below the bloom threshold: the physically-based halo is 10–50× white near the sun and would smear the whole frame
sky.material.onBeforeCompile = sh => { sh.fragmentShader = sh.fragmentShader.replace('gl_FragColor = vec4( texColor, 1.0 );', 'gl_FragColor = vec4( min( texColor * 0.85, vec3( 1.2 ) ), 1.0 );'); };
{ const envScene = new THREE.Scene(); envScene.add(sky); const pm = new THREE.PMREMGenerator(renderer); scene.environment = pm.fromScene(envScene).texture; pm.dispose(); }
scene.add(sky);
export const hemi = new THREE.HemisphereLight('#b8a9d8', '#3f3328', 0.55); scene.add(hemi);
export const sun = new THREE.DirectionalLight('#ffc28a', 2.6);
sun.position.copy(sunDir).multiplyScalar(1200); sun.castShadow = true;
sun.shadow.mapSize.set(2048,2048);
Object.assign(sun.shadow.camera, {left:-620,right:620,top:620,bottom:-620,near:200,far:2600});
sun.shadow.bias = -0.0004; sun.shadow.normalBias = 0.6; sun.shadow.camera.updateProjectionMatrix();
scene.add(sun); scene.add(sun.target);
export const texLoader = new THREE.TextureLoader();
export const TEX = '/tex/';
{ const flare = new Lensflare();
  texLoader.load(TEX+'lensflare/lensflare0.png', t => { flare.addElement(new LensflareElement(t, 240, 0, new THREE.Color('#8a6a48'))); });
  texLoader.load(TEX+'lensflare/lensflare3.png', t => { flare.addElement(new LensflareElement(t, 60, 0.6)); flare.addElement(new LensflareElement(t, 90, 0.75)); flare.addElement(new LensflareElement(t, 140, 1.0)); });
  sun.add(flare); }


export function groundTexture(){
  const c = document.createElement('canvas'); c.width = c.height = 256; const g = c.getContext('2d');
  g.fillStyle = '#4a4a50'; g.fillRect(0,0,256,256);
  for (let i=0;i<9000;i++){ g.fillStyle = `rgba(${Math.random()<.5?0:255},${Math.random()<.5?0:255},${Math.random()<.5?0:255},${0.03+Math.random()*0.05})`; g.fillRect(Math.random()*256, Math.random()*256, 1.5, 1.5); }
  const t = new THREE.CanvasTexture(c); t.wrapS = t.wrapT = THREE.RepeatWrapping; t.repeat.set(140,140); t.colorSpace = THREE.SRGBColorSpace; return t;
}
export const ground = new THREE.Mesh(new THREE.PlaneGeometry(6000,6000), new THREE.MeshStandardMaterial({map:groundTexture(), color:'#8c8c92', roughness:1}));
ground.rotation.x = -Math.PI/2; ground.receiveShadow = true; scene.add(ground);

export const composer = new EffectComposer(renderer);
export const renderPass = new RenderPass(scene, camera); composer.addPass(renderPass);
export const gtao = new GTAOPass(scene, camera, innerWidth, innerHeight); gtao.output = GTAOPass.OUTPUT.Default; gtao.enabled = false;
gtao.updateGtaoMaterial({radius:14, distanceExponent:1, thickness:2, scale:1.2, samples:12, distanceFallOff:1, screenSpaceRadius:false});
gtao.updatePdMaterial({lumaPhi:10, depthPhi:2, normalPhi:3, radius:4, radiusExponent:1, rings:2, samples:16});
gtao.blendIntensity = 0.8; composer.addPass(gtao);
export const bloom = new UnrealBloomPass(new THREE.Vector2(innerWidth, innerHeight), 0.42, 0.45, 1.35); composer.addPass(bloom);
export const bokeh = new BokehPass(scene, camera, {focus:420, aperture:0.00009, maxblur:0.008}); bokeh.enabled = false; composer.addPass(bokeh);
composer.addPass(new OutputPass());
export const GradeShader = {
  uniforms:{ tDiffuse:{value:null}, time:{value:0}, heat:{value:0}, heatC:{value:new THREE.Vector2(.5,.5)}, aspect:{value:1}, grain:{value:0.035}, vig:{value:0.55}, warm:{value:1} },
  vertexShader:`varying vec2 vUv; void main(){ vUv = uv; gl_Position = projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
  fragmentShader:`uniform sampler2D tDiffuse; uniform float time, heat, aspect, grain, vig, warm; uniform vec2 heatC; varying vec2 vUv;
    float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1,311.7)))*43758.5453); }
    void main(){
      vec2 uv = vUv;
      // heat shimmer around the impact point while firing
      if (heat > 0.001){ vec2 d = (uv - heatC)*vec2(aspect,1.0); float r = length(d); float k = smoothstep(0.2, 0.0, r)*heat;
        uv += k*0.003*vec2(sin(uv.y*90.0 + time*18.0), cos(uv.x*80.0 + time*15.0)); }
      // chromatic aberration, stronger at the edges
      vec2 c = uv - 0.5; float rr = dot(c,c);
      float ab = 0.0008 + 0.0022*rr + 0.002*heat;
      vec3 col; col.r = texture2D(tDiffuse, uv + c*ab).r; col.g = texture2D(tDiffuse, uv).g; col.b = texture2D(tDiffuse, uv - c*ab).b;
      // teal-orange grade: cool shadows, warm highlights, gentle S-curve
      float l = dot(col, vec3(0.299,0.587,0.114));
      vec3 shadowsT = vec3(0.86, 0.93, 1.08), highsT = vec3(1.06, 1.0, 0.92);
      col *= mix(shadowsT, highsT, smoothstep(0.1, 0.8, l));
      col = mix(col, vec3(l), -0.12);                       // +12% saturation
      col = col*col*(3.0-2.0*col)*0.35 + col*0.65;          // soft contrast
      col = mix(col, col*vec3(1.0,0.97,0.93), warm*0.5);
      // vignette + grain
      float v = 1.0 - vig*smoothstep(0.25, 1.15, length(c*vec2(aspect*0.85,1.0)));
      col *= v;
      col += (hash(uv*vec2(1920.0,1080.0) + fract(time)*vec2(13.7,7.3)) - 0.5)*grain*(1.0-l*0.5);
      gl_FragColor = vec4(col, 1.0);
    }`
};
export const grade = new ShaderPass(GradeShader); composer.addPass(grade);
export const smaa = new SMAAPass(); composer.addPass(smaa);
addEventListener('resize', () => { camera.aspect = innerWidth/innerHeight; camera.updateProjectionMatrix(); renderer.setSize(innerWidth, innerHeight); composer.setSize(innerWidth, innerHeight); smaa.setSize(innerWidth*renderer.getPixelRatio(), innerHeight*renderer.getPixelRatio()); });
export const zeroM = new THREE.Matrix4().makeScale(0,0,0);
